import React, { useMemo } from "react";
import { useState, useContext, useCallback } from "react";
import { PageWrapper } from "../UI/Grid";
import _ from "lodash";
import { useNavigate } from "react-router-dom";
import { getUsers, deleteUsers } from "../../requests/userManagementRequests";
import Table, { useTableSelections } from "../UI/Table";
import Card from "../UI/Card";
import { Body } from "../UI/Text";
import styled, { useTheme } from "styled-components";
import Button from "../UI/Button";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import { faTrash } from "@fortawesome/free-solid-svg-icons";
import Modal from "../UI/Modal";
import { useQuery } from "../Hooks";
import UserContext from "../../context/user/UserContext";
import { Flex } from "reflexbox";
import Indicator from "../UI/Indicator";

const LoggedInIcon = ({ username, isLoggedIn }) => {
  const { loggedInUsers } = useContext(UserContext);

  return (
    <Indicator
      id={username}
      active={
        (isLoggedIn === true && !loggedInUsers[username]) ||
        loggedInUsers[username] === true
      }
    />
  );
};

const Users = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const userContext = useContext(UserContext);
  const { pid, roleLevel } = userContext?.activeUser || {};

  const { data, isLoading, refetchQuery } = useQuery({
    queryID: "users",
    request: getUsers,
    queryVariables: { pid: roleLevel === 9 ? undefined : pid },
    skip: !userContext.loggedInUsers,
  });

  const { rows = [], count, roles = [], platforms = [] } = data || {};

  const getIcon = (roleLevel) => {
    switch (roleLevel) {
      case 9:
        return "faUserAstronaut";
      case 5:
        return "faUserTie";
      case 1:
        return "faUserCheck";
      default:
        return "faPeopleGroup";
    }
  };

  const gridData = rows.map((user) => ({
    id: user.id,
    title: `${user.firstName} ${user.lastName}`,
    subtitle: user.username,
    original: { username: user.username },
    iconName: getIcon(user?.roleLevel || 0),
  }));

  const getRoleName = useCallback(
    (roleId) => {
      const roleName = _.find(roles, (role) => role.id === roleId)?.name || "—";
      return roleName;
    },
    [roles]
  );

  const columns = useMemo(() => {
    const cols = [
      {
        id: "username",
        data: ({ username }) => <Body ellipsis>{username || "—"}</Body>,
        header: "Email",
        size: { md: 4, sm: 3, xs: 5 },
      },

      {
        id: "lastName",
        data: ({ firstName, lastName }) => (
          <Body center ellipsis>
            {!!firstName ? `${firstName} ` : ""}
            {lastName || "—"}
          </Body>
        ),
        header: "Last Name",
        size: { md: 3, sm: -1, xs: -1 },
      },
      {
        id: "isLoggedIn",
        data: ({ username, isLoggedIn }) => {
          return <LoggedInIcon username={username} isLoggedIn={isLoggedIn} />;
        },
        header: "Online",
        size: { lg: 2, md: 2, sm: 2, xs: 3 },
      },
      {
        id: "roleId",
        data: ({ roleId }) => <Body center>{getRoleName(roleId)}</Body>,
        header: "Role Level",
        size: { md: 2, sm: -1, xs: -1 },
      },

      {
        id: "id",
        data: "id",
        header: "Id",
        size: -1,
      },
    ];
    if (roleLevel === 9)
      cols.push({
        id: "pid",
        data: ({ pid }) => {
          const userPlatform = _.find(platforms, ({ id }) => pid === id);
          const { name } = userPlatform || {};
          return <Body center>{name || "—"}</Body>;
        },
        header: "Platform",
        size: { md: 2, sm: 3, xs: 5 },
      });
    return cols;
  }, [getRoleName, platforms, roleLevel]);

  const onRowClick = (row) => {
    const { username } = row?.original || {};
    if (username) navigate(`/Users/edit/${username}`);
  };

  const selections = useTableSelections("id");

  const [deleting, setDeleting] = useState(false);

  const onConfirmDelete = async () => {
    await deleteUsers({
      users: selections.rows
        .map((row) => ({ id: row.id, username: row.username }))
        .filter((row) => row.id !== userContext.activeUser?.id),
      self: userContext.activeUser?.username,
    });
    selections.actions.clear();
    setDeleting(false);

    refetchQuery("users");
  };

  const subtitle = useMemo(() => {
    const { roleLevel = 0 } = userContext.activeUser || {};
    if (roleLevel === 9)
      return "Due to your role as a Super Admin, you are able to view all users across all platforms.";
    else return "Below is a list of all of the users of your Platform.";
  }, [userContext.activeUser]);

  return (
    <PageWrapper>
      {/* {selections.rows.length > 0 ? (
        <StyledRow style={{ padding: "1rem 0px 0px 0px !important" }}>
          <Flex md={6}>
            <StyledCard interactive={false}>
              <Body>
                {selections.rows.length} user
                {selections.rows.length !== 1 ? "s" : ""} selected
              </Body>
              <div>
                <Flex onClick={(_) => setDeleting(true)} size={24}>
                  <FontAwesomeIcon icon={faTrash} />
                </Flex>
              </div>
            </StyledCard>
          </Flex>
        </StyledRow>
      ) : null} */}
      <Table
        createButtonText="Create User"
        createButtonIcon="faUserPlus"
        createLink="/Users/create"
        title="Users"
        subtitle={subtitle}
        data={rows}
        columns={columns}
        refetchData={refetchQuery}
        hideRefetch={false}
        onRowClick={onRowClick}
        onCardClick={onRowClick}
        gridData={gridData}
        backButtonLink="/Administration"
        // selections={selections}
        isLoading={isLoading}
        totalCount={count}
      />
      <Modal show={deleting} setShow={setDeleting}>
        <ModalContainer>
          <Card
            disableHover
            style={{ display: "block", borderStyle: "none", padding: "1rem" }}
          >
            <ModalTitle>
              Are you sure you want to delete the following users?
            </ModalTitle>
            <DeleteList>
              <div>
                {selections.rows.map((row) => (
                  <Body key={row.id} xs={6}>
                    {row.username}
                  </Body>
                ))}
              </div>
            </DeleteList>
            <span
              style={{
                textAlign: "center",
                justifyContent: "center",
                alignItems: "center",
                margin: "1rem",
              }}
            >
              <Button color={theme.success} onClick={onConfirmDelete}>
                Yes
              </Button>
              <Button color={theme.danger} onClick={() => setDeleting(false)}>
                No
              </Button>
            </span>
          </Card>
        </ModalContainer>
      </Modal>
    </PageWrapper>
  );
};

// const StyledRow = styled(Flex)`
//   margin: 0px !important;
//   padding: 1rem 0px 0px 0px !important;
//   align-items: flex-end;
// `;

// const StyledCard = styled(Card)`
//   padding: 0.5rem 1rem;
//   margin-right: 1rem;
//   flex-direction: row;
//   justify-content: space-between;
// `;

const DeleteList = styled.div`
  overflow-y: auto;
  height: fit-content;
  max-height: 12rem;
  margin: 1rem;
  padding: 0.25rem;
`;

const ModalTitle = styled(Body)`
  font-size: 24px;
  text-align: center;
  margin: 1rem;
`;

const ModalContainer = styled(Flex)`
  padding: 1rem !important;
  margin: auto;
  display: flex;
  width: 80%;
  height: 60%;
  background-color: ${({ theme }) => theme.backgroundColor};
  border: 1px solid ${({ theme }) => theme.borderColor || "gray"};
  border-radius: ${({ theme }) => theme.borderRadius || "15px"};
`;

export default Users;
